import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

const APP_NAME = "Release Channel";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/views/DashboardView.vue"),
    meta: { title: "Dashboard" },
  },
  {
    path: "/software",
    name: "Software",
    component: () => import("@/views/SoftwareView.vue"),
    meta: { title: "Software" },
  },
  {
    path: "/software/:id",
    name: "SoftwareDetail",
    component: () => import("@/views/SoftwareDetailView.vue"),
    // Title is patched in afterEach with the resolved software_id so
    // multi-tab users can tell which software they're looking at.
    meta: { title: "Software" },
    props: true,
  },
  {
    path: "/software/:id/register",
    name: "SoftwareRegister",
    component: () => import("@/views/RegisterReleaseView.vue"),
    meta: { title: "Register release" },
    props: true,
  },
  {
    path: "/channels",
    name: "Channels",
    component: () => import("@/views/ChannelsView.vue"),
    meta: { title: "Channels" },
  },
  {
    path: "/trains",
    name: "Trains",
    component: () => import("@/views/TrainsView.vue"),
    meta: { title: "Trains" },
  },
  // The /calendar standalone route was folded into DashboardView on
  // 2026-05-27 — the channel calendar widget now lives at the bottom
  // of the dashboard so the home page is the single landing surface.
  // Releases / Promotions / Builds list views were removed in the
  // 2026-05-21 v1↔v2 API merge. The legacy flat-row endpoints they
  // consumed (/api/v1/releases, /api/v1/promotions, /api/v1/builds) no
  // longer exist; the Manifest-shaped replacement at
  // /api/v1/releases/{software} will get its own dedicated SPA surface
  // in a follow-up MR. Until then the dashboard and channel pointers
  // remain the primary read-only views.
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Opportunistically stash a Passgate token from the URL hash if present
// (Passgate's hash-fragment redirect leaves `#access_token=XXX&...` after
// login). Backend API auth is gated separately by Passgate via FastAPI
// deps, so the SPA does not block routing on it. The previous version of
// this guard hard-redirected to a non-existent `/login` route when
// localStorage had no token, which dead-ended the SPA at a blank page.
//
// Implemented as a one-shot side effect at module load (not inside a
// Router guard) so that the guard's call signature stays trivially safe
// — Vue Router 4 deeply inspects the return value of guards and an
// over-customised guard previously surfaced as
//   "TypeError: Cannot read properties of undefined (reading 'path')".
if (typeof window !== "undefined" && window.location.hash.includes("access_token=")) {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("access_token");
  if (token) {
    localStorage.setItem("access_token", token);
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

// WCAG 2.4.2 ('Page Titled'): keep the browser tab in sync with the
// active route so users with multiple tabs (and screen-reader users
// who hear the title on navigation) can tell the views apart. Done as
// an afterEach (not in a guard) so it never blocks navigation.
router.afterEach((to) => {
  if (typeof document === "undefined") return;
  let sub = (to.meta?.title as string | undefined) ?? (to.name as string | undefined);
  // Software detail is the only route with a meaningful path param —
  // suffix it so multi-tab users can tell `/software/foo` from
  // `/software/bar` without clicking through.
  if (to.name === "SoftwareDetail" && typeof to.params.id === "string") {
    sub = `${to.params.id} · Software`;
  }
  document.title = sub ? `${sub} · ${APP_NAME}` : APP_NAME;
});

export default router;
